import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { CurvedText } from '../../../components/CurvedText';
import { Counter } from '../Counter';
import { Dialog } from '../Dialog';

import { ScWheel } from './styled';

interface WheelProps {
  id?: string;
  spin?: number;
  sideContent?: {
    left: string;
    right: string;
  };
  wheelImage?: string;
  pointerImage?: string;
  wheelBackImage?: string;
  backPointer?: string;
  translation: string;
  isRotated?: string;
  darkenImage?: string;
  mainRadialGradient?: string;
  spinsLeftImage?: string;
  openModal?: (
    valueSpins: Dispatch<SetStateAction<number>>,
    valueButton: Dispatch<SetStateAction<boolean>>
  ) => void;
  handleSpin?: () => void;
  onAnimationEnd?: () => void;
  handleModalState?: () => void;
  spinBtnBg?: string;
  link?: string;
  isCounter?: boolean;
  isLongOffer?: boolean;
}

const WheelContent: FC<
  Pick<WheelProps, 'translation' | 'spin' | 'isRotated'>
> = ({ spin, translation }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(translation);

  return (
    <div className={`content spin-${spin}`}>
      {(
        Object.values(t('wheelContents', { returnObjects: true })) as string[]
      ).map((_, index, array) => {
        let text = array[index + 1];

        if (spin! > 0 && index === 0) text = array[0];

        return (
          <p
            className={`${language}`}
            dangerouslySetInnerHTML={{ __html: text }}
            key={index}
          />
        );
      })}
    </div>
  );
};

const InsideWheel: FC<PropsWithChildren<WheelProps>> = ({
  id,
  spin,
  children,
  wheelImage,
  pointerImage,
  onAnimationEnd,
  translation,
  darkenImage,
}) => {
  const { t } = useTranslation(translation);

  return (
    <div className={`wheel__${id}`}>
      <div className="wheel__img-wrapper">
        <CurvedText
          objectSize={700}
          offset={30}
          overlap={true}
          spacing={12}
          text={t('yourPrize')}
        />
        {darkenImage && (
          <img alt="Darken" className="wheel__darken" src={darkenImage} />
        )}
        <img
          alt="Pointer"
          className={`wheel__pointer ${id}`}
          src={pointerImage}
        />
        <img
          alt="Spin me"
          className={`wheel__circle_${spin}`}
          onAnimationEnd={onAnimationEnd}
          src={wheelImage}
        />
        {children}
      </div>
    </div>
  );
};

export const Wheel: FC<WheelProps> = ({
  wheelImage,
  pointerImage,
  backPointer,
  wheelBackImage,
  translation,
  darkenImage,
  mainRadialGradient,
  sideContent,
  spinsLeftImage,
  openModal,
  handleModalState,
  spinBtnBg,
  link,
  isCounter = true,
  isLongOffer,
}) => {
  const { t } = useTranslation(translation);
  const [spin, setSpin] = useState(0);
  const [isRotated, setIsRotated] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const animationTimeout1 = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimeout2 = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSpin = () => {
    if (spin > 1) {
      handleModalState!();
    }
    setIsButtonDisabled(true);
    setIsRotated(false);
    setSpin((prev) => prev + 1);
  };

  const handleAnimationEnd = () => {
    if (animationTimeout1.current) {
      clearTimeout(animationTimeout1.current);
    }
    if (animationTimeout2.current) {
      clearTimeout(animationTimeout2.current);
    }
    animationTimeout1.current = setTimeout(() => {
      if (openModal) {
        openModal(setSpin, setIsButtonDisabled);
        return;
      }
      setIsRotated(true);
    }, 500);

    animationTimeout2.current = setTimeout(() => {
      setIsButtonDisabled(false);
    }, 1500);
  };

  useEffect(
    () => () => {
      if (animationTimeout1.current) clearTimeout(animationTimeout1.current);
      if (animationTimeout2.current) clearTimeout(animationTimeout2.current);
    },
    []
  );

  return (
    <>
      <ScWheel
        isLongOffer={isLongOffer}
        mainRadialGradient={mainRadialGradient as string}
        spinBtnBg={spinBtnBg}
      >
        <div className={`wheel__outside ${isRotated ? 'rotate' : ''}`}>
          {sideContent && (
            <div className="wheel__side left">
              <h1>{t('spin')}</h1>
              <img alt="Left content" src={sideContent?.left} />
            </div>
          )}
          <InsideWheel
            darkenImage={darkenImage}
            handleSpin={handleSpin}
            id="front"
            onAnimationEnd={handleAnimationEnd}
            pointerImage={pointerImage}
            spin={spin}
            translation={translation}
            wheelImage={wheelImage}
          >
            <WheelContent spin={spin} translation={translation} />
            <div className="wheel__center">
              <button
                className="wheel__btn"
                disabled={isButtonDisabled}
                onClick={handleSpin}
              >
                {t('spinBtn')}
              </button>
            </div>
          </InsideWheel>
          <InsideWheel
            id="back"
            pointerImage={backPointer}
            translation={translation}
            wheelImage={wheelBackImage}
          >
            <Dialog
              handleSpin={handleSpin}
              isButtonDisabled={isButtonDisabled}
              isLongOffer={isLongOffer}
              link={link}
              spin={spin}
              translation={translation}
            />
          </InsideWheel>
          {sideContent && (
            <div className="wheel__side right">
              <h1>{t('andGet')}</h1>
              <img alt="Right content" src={sideContent?.right} />
            </div>
          )}
        </div>
      </ScWheel>
      {isCounter && (
        <Counter
          image={spinsLeftImage}
          mainRadialGradient={mainRadialGradient as string}
          spin={spin}
          spinBtnBg={spinBtnBg}
          translation={translation}
        />
      )}
    </>
  );
};
